import {
  Box,
  Grid,
  Divider,
  Checkbox,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import FormLabel from "@mui/material/FormLabel";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useRecoilState } from "recoil";
import { Info, InfoOutlined } from "@mui/icons-material";
import {
  CheckboxLabelWrapper,
  NewReqCheckboxLabelRow,
  StyledBox,
  StyledFormLabelClinical,
  StyledSectionHeadingClinical,
  BoldText,
  SoloCheckboxWrapper,
  NewReqTooltip,
  NewReqTooltipContent,
  NewReqHelperTextBox,
  NewReqHelperTextLines,
  NewReqTestOptionLabelColumn,
  NewReqTestOptionRow,
  NewReqPatientTestPanelCheckboxTitleWrapper,
  StyledRedSpan,
  NewReqPatientTestPanelRhdOnlyCheckboxWrapper,
  NewReqNoteMessage,
  TestPanelNestedStack,
  TestPanelSectionStack,
  HelperCopy,
  OptionGenes,
  OptionLabel,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { NewRequisitionForm } from "../../recoil/types";
import {
  formatWithTimezoneOffset,
  getWeeksDiffGestation,
  parseYmdAtNoonUtc,
} from "../../utils/helpers";
import { NewRequisitionCFModal } from "./NewRequisitionCFModal";
import NewReqClinicalIndicationAneuploidyChoice from "./NewReqClinicalIndicationAneuploidyChoice";
import NewReqClinicalIndicationAneuploidyFetalAntigen from "./NewReqClinicalIndicationAneuploidyFetalAntigen";
import { EXPANDED_CARRIER_PANEL, phlebotomyLanguageOptions } from "./types";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import CarrierScreenPanelSelect, {
  getCarrierPanelSelectionPatch,
  type CarrierPanelSelection,
} from "./CarrierScreenPanelSelect";
import UnityCompleteBundleSelect from "./UnityCompleteBundleSelect";
import {
  aquaDividerSpacedSx,
  aquaDividerTightSx,
  testPanelRadioSx,
  aneuploidyVariantDividerSx,
  carrierSectionTopGapSx,
  checkboxLabelRowMarginSx,
  cfDnaCheckboxSx,
  cfDnaInfoIconSx,
  cfDnaOptOutRowSx,
  cfDnaTooltipPopperSx,
  clickableClinicalLabelSx,
  columnFlexSx,
  columnStackSx,
  flushCheckboxSx,
  flushTopSx,
  getAneuploidyLabelColumnSx,
  getAneuploidyLabelCursorSx,
  getCfDnaOptOutWrapperSx,
  getDisabledOpacitySx,
  getNestedAddOnColumnSx,
  getPhlebotomyLanguageRowSx,
  getRhdLabelPointerSx,
  gridBottomPaddingSx,
  lightFormLabelSx,
  otherLanguageFieldSx,
  phlebotomyBoxSx,
  phlebotomyLanguageSelectSx,
  unselectedCheckboxSx,
} from "./testPanelStyles";
import {
  getAvailableUnityCompleteBundleOptions,
  getVisibleExpandedCarrierPanels,
  getUnityCompleteBundlePatch,
  type UnityCompleteBundleKey,
} from "./unityCompleteBundles";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { WarningIcon } from "../../assets/iconComponents/Warning";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { useCarrierScreenAttachmentVariant } from "./useCarrierScreenAttachmentVariant";
import { GetTypographyContent } from "../shared/Typography/Typography";

export const getAneuploidyIneligibleMessage = (
  requisitionForm: NewRequisitionForm,
  minimumWeeks = 9,
): ReactNode => {
  const weeksPregnant = requisitionForm.estimatedDueDate
    ? getWeeksDiffGestation(
        parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
        new Date(),
      )
    : 0;
  if (requisitionForm.sex === "Male") {
    return "Aneuploidy NIPT is only available to patients that are currently pregnant.";
  }
  if (requisitionForm.notPregnant) {
    return `Aneuploidy NIPT is only available to patients that are currently pregnant (≥ ${minimumWeeks} weeks gestation).`;
  }
  if (requisitionForm.isTripleOrMore) {
    return "Aneuploidy NIPT is only available to singleton and twin gestations.";
  }
  if (requisitionForm.isTwin && requisitionForm.isEggDonor) {
    return "Aneuploidy NIPT is not available for twin gestations achieved with egg donation.";
  }
  if (!requisitionForm.notPregnant && weeksPregnant < minimumWeeks) {
    return (
      <>
        <span>{`The patient's due date is `}</span>
        <StyledRedSpan>
          {formatWithTimezoneOffset(requisitionForm.estimatedDueDate)}
        </StyledRedSpan>
        {`. As a result, the patient's gestational age is `}
        <StyledRedSpan>{`< ${minimumWeeks} weeks. `}</StyledRedSpan>
        Aneuploidy NIPT is performed for pregnancies ≥ {minimumWeeks} weeks.
        <StyledRedSpan>
          {` Please ensure to collect the patient's blood sample when the
            pregnancy is ≥ ${minimumWeeks} weeks.`}
        </StyledRedSpan>
      </>
    );
  }
  if (requisitionForm.disableAneuploidy) {
    return "Patient is ineligible for Aneuploidy tests.";
  }
  return "";
};

const ClinicalIndicationTestPanel = () => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const { requisitionForm, preflight } = newRequisition;
  const [cfModalOpen, setCFModalOpen] = useState(false);
  const { deviceType } = useDeviceHook();
  const isPhlebotomyLanguageSelectEnabled = useBooleanFeatureFlag(
    FeatureFlags.PhlebotomyLanguageSelect,
  );
  const visibleExpandedCarrierPanels = getVisibleExpandedCarrierPanels(
    requisitionForm,
    requisitionForm.expandedCarrierPanel,
  );
  // Regular 5- and 13-gene presets remain available without expanded-panel
  // account configuration, so show the selector whenever any preset is valid.
  const availableUnityCompleteBundles =
    getAvailableUnityCompleteBundleOptions(requisitionForm);
  const showUnityComplete = availableUnityCompleteBundles.length > 0;
  const { auth } = useAuthenticationHook();
  const { variant: attachmentVariant } = useCarrierScreenAttachmentVariant();
  // "note" A/B variant: red note after the Carrier Screen section in the
  // Aneuploidy-first layout while only Aneuploidy is selected.
  // Hidden in preflight (Confirm Details renders this panel read-only).
  const showCarrierScreenNote =
    attachmentVariant === "note" &&
    !preflight &&
    requisitionForm.aneuploidyNIPT &&
    !requisitionForm.carrierScreen &&
    !requisitionForm.isRhdOnlyOrder &&
    !requisitionForm.disableAneuploidy &&
    !getAneuploidyIneligibleMessage(requisitionForm);

  // Exposure fires the first time the note becomes visible on this visit.
  const noteExposureTracked = useRef(false);
  useEffect(() => {
    if (showCarrierScreenNote && !noteExposureTracked.current) {
      noteExposureTracked.current = true;
      TrackAnalyticsEvent(
        AnalyticsEventNames.CarrierScreenAttachmentNoteShown,
        auth?.token,
        auth?.username,
        { variant: attachmentVariant },
      );
    }
  }, [showCarrierScreenNote]);

  const isOtherLanguageSelected = requisitionForm.preferredLanguage === "Other";
  const shouldShowLanguageDropdown =
    !preflight || Boolean(requisitionForm.preferredLanguage);
  const aneuploidyIneligibleMessage = getAneuploidyIneligibleMessage(
    requisitionForm,
    requisitionForm.isTwin ? 9 : 8,
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        [name]: checked,
      },
    }));
  };

  const onCarrierPanelSelect = (selection: CarrierPanelSelection | null) => {
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        ...getCarrierPanelSelectionPatch(selection),
      },
    }));
  };

  const onUnityCompleteBundleSelect = (key: UnityCompleteBundleKey) => {
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        ...getUnityCompleteBundlePatch(key),
      },
    }));
  };

  const onCheckboxLabelClick = (name: string, value: boolean) => {
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        [name]: value,
      },
    }));
  };

  const aneuploidyDisabled =
    requisitionForm.disableAneuploidy || requisitionForm.isRhdOnlyOrder;
  const isFiveGenePanelSelected =
    requisitionForm.carrierScreen &&
    requisitionForm.expandedCarrierPanel === null;
  const is130GenePanelSelected =
    requisitionForm.carrierScreen &&
    requisitionForm.expandedCarrierPanel === EXPANDED_CARRIER_PANEL.CARRIER_130;
  // Add-ons require a standard carrier panel and are disabled for expanded
  // panels, which cannot be combined with Fragile X or Plus.
  const carrierAddOnsDisabled =
    !requisitionForm.carrierScreen ||
    requisitionForm.expandedCarrierPanel !== null;
  const isRhdOrderBlocked =
    requisitionForm.carrierScreen ||
    requisitionForm.aneuploidyNIPT ||
    requisitionForm.sex === "Male";
  const carrierHeadingOpacitySx = getDisabledOpacitySx(
    requisitionForm.isRhdOnlyOrder,
  );
  const maleSexOpacitySx = getDisabledOpacitySx(requisitionForm.sex === "Male");
  const rhdBlockedOpacitySx = getDisabledOpacitySx(isRhdOrderBlocked);
  const carrierAddOnsSx = getNestedAddOnColumnSx(
    deviceType,
    carrierAddOnsDisabled,
  );

  const handleCfDnaOptOutChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (requisitionForm.cfDNAOptOut) {
      onChange(e);
      return;
    }
    setCFModalOpen(true);
  };

  const handleRhdLabelClick = () => {
    onCheckboxLabelClick("isRhdOnlyOrder", !requisitionForm.isRhdOnlyOrder);
  };

  const setAneuploidyVariant = (include22q: boolean, checked: boolean) => {
    if (aneuploidyDisabled) {
      return;
    }
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        aneuploidyNIPT: checked,
        is22q: checked && include22q,
        Z360: checked,
      },
    }));
  };

  const handlePhlebotomyLabelClick = () => {
    onCheckboxLabelClick(
      "isMobilePhlebotomy",
      !requisitionForm.isMobilePhlebotomy,
    );
  };

  const handlePreferredLanguageChange = (e: { target: { value: string } }) => {
    const preferredLanguage = e.target
      .value as typeof requisitionForm.preferredLanguage;
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        preferredLanguage,
        otherLanguage:
          preferredLanguage !== "Other"
            ? ""
            : current.requisitionForm.otherLanguage,
      },
    }));
  };

  const handleOtherLanguageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const otherLanguage = e.target.value;
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        otherLanguage,
      },
    }));
  };

  const renderCfDnaOptOutCheckbox = ({
    indent = false,
  }: { indent?: boolean } = {}) => (
    <Box sx={getCfDnaOptOutWrapperSx(deviceType, indent)}>
      <Box sx={cfDnaOptOutRowSx}>
        <OptionLabel $tone="muted">cfDNA fetal risk assay opt-out</OptionLabel>
        <NewReqTooltip
          PopperProps={{
            sx: cfDnaTooltipPopperSx,
          }}
          title={
            <NewReqTooltipContent>
              If opting out of cfDNA fetal risk assay, a separate cfDNA assay
              will <span>NOT</span> be run for carrier positive singleton
              pregnancies.
            </NewReqTooltipContent>
          }
        >
          <Info className="cf-info" sx={cfDnaInfoIconSx} />
        </NewReqTooltip>
        <Checkbox
          sx={cfDnaCheckboxSx}
          checked={requisitionForm.cfDNAOptOut}
          name="cfDNAOptOut"
          inputProps={{
            "aria-label": "cfDNA fetal risk assay opt-out",
          }}
          onChange={handleCfDnaOptOutChange}
        />
      </Box>
    </Box>
  );

  const renderPlusPanelCheckbox = () => (
    <NewReqTestOptionRow $nested>
      <NewReqTestOptionLabelColumn>
        <CheckboxLabelWrapper
          label={
            <>
              <OptionLabel>
                Add <strong>Plus Panel</strong>
              </OptionLabel>
              <OptionGenes>
                ACADM • ASPA • DHCR7 • DMD • ELP1 • HEXA • PAH • PMM2
              </OptionGenes>
            </>
          }
          control={
            <Checkbox
              sx={unselectedCheckboxSx}
              checked={requisitionForm.isAcogPlus}
              disabled={carrierAddOnsDisabled}
              name="isAcogPlus"
              onChange={onChange}
            />
          }
        />
      </NewReqTestOptionLabelColumn>
    </NewReqTestOptionRow>
  );

  const renderAneuploidyAddOns = (active: boolean) => {
    const addOnsDisabled = !active || requisitionForm.disableAneuploidy;

    return (
      <Box sx={columnStackSx}>
        <NewReqClinicalIndicationAneuploidyChoice active={active} />
        <NewReqClinicalIndicationAneuploidyFetalAntigen active={active} />
        <Box sx={getNestedAddOnColumnSx(deviceType, addOnsDisabled)}>
          <CheckboxLabelWrapper
            label={
              <OptionLabel $tone="alert">
                Opt out <strong>Fetal sex</strong> (Includes aneuploidy NIPT)
              </OptionLabel>
            }
            control={
              <Checkbox
                sx={unselectedCheckboxSx}
                checked={active && requisitionForm.aneuploidyOptOutFetalSex}
                disabled={addOnsDisabled}
                name="aneuploidyOptOutFetalSex"
                onChange={onChange}
              />
            }
          />
        </Box>
      </Box>
    );
  };

  const renderAneuploidyVariant = (
    include22q: boolean,
    showIneligibleMessage: boolean,
  ) => {
    const active =
      requisitionForm.aneuploidyNIPT &&
      Boolean(requisitionForm.is22q) === include22q;
    const label = include22q
      ? "Aneuploidy NIPT + 22q11.2 Microdeletion"
      : "Aneuploidy NIPT";

    return (
      <Box>
        <NewReqCheckboxLabelRow sx={checkboxLabelRowMarginSx}>
          <SoloCheckboxWrapper>
            <Radio
              id={include22q ? "new-test-ci-aneu-with-22q" : "new-test-ci-aneu"}
              sx={testPanelRadioSx}
              checked={active}
              name="aneuploidyVariant"
              value={include22q ? "with-22q" : "without-22q"}
              inputProps={{ "aria-label": label }}
              onClick={() => setAneuploidyVariant(include22q, !active)}
              disabled={aneuploidyDisabled}
            />
          </SoloCheckboxWrapper>
          <Box sx={getAneuploidyLabelColumnSx(aneuploidyDisabled)}>
            <StyledFormLabelClinical
              sx={getAneuploidyLabelCursorSx(aneuploidyDisabled)}
              onClick={() => setAneuploidyVariant(include22q, !active)}
            >
              <OptionLabel>
                <strong>{label}</strong>
              </OptionLabel>
              <OptionGenes>
                T13 • T18 • T21{include22q ? " • 22q11.2" : ""}
              </OptionGenes>
            </StyledFormLabelClinical>
            {showIneligibleMessage && aneuploidyIneligibleMessage && (
              <NewReqHelperTextBox>
                <InfoOutlined />
                <NewReqHelperTextLines>
                  <HelperCopy $size="small">
                    {aneuploidyIneligibleMessage}
                  </HelperCopy>
                </NewReqHelperTextLines>
              </NewReqHelperTextBox>
            )}
          </Box>
        </NewReqCheckboxLabelRow>
        {renderAneuploidyAddOns(active)}
      </Box>
    );
  };

  return (
    <>
      <StyledBox>
        <NewRequisitionCFModal
          cfModalOpen={cfModalOpen}
          setCFModalOpen={setCFModalOpen}
        />
        <Grid
          container
          spacing={3}
          columnSpacing={0}
          sx={gridBottomPaddingSx}
          columns={{ xs: 4, sm: 12 }}
        >
          {showUnityComplete && (
            <>
              <Grid item xs={12}>
                <UnityCompleteBundleSelect
                  form={requisitionForm}
                  disabled={aneuploidyDisabled}
                  onSelect={onUnityCompleteBundleSelect}
                />
              </Grid>

              {!requisitionForm.isRhdOnlyAccount && (
                <Grid item xs={12} sx={flushTopSx}>
                  <Divider sx={aquaDividerSpacedSx} />
                </Grid>
              )}
            </>
          )}

          {requisitionForm.isRhdOnlyAccount && (
            <>
              <Grid item xs={12}>
                <div>
                  <NewReqCheckboxLabelRow sx={checkboxLabelRowMarginSx}>
                    <SoloCheckboxWrapper>
                      <Checkbox
                        id="new-test-ci-rhd-only"
                        sx={flushCheckboxSx}
                        checked={requisitionForm.isRhdOnlyOrder}
                        name="isRhdOnlyOrder"
                        inputProps={{ "aria-label": "RhD Only" }}
                        onChange={onChange}
                        disabled={isRhdOrderBlocked}
                      />
                    </SoloCheckboxWrapper>
                    <Box sx={columnFlexSx}>
                      <StyledFormLabelClinical
                        sx={getRhdLabelPointerSx(isRhdOrderBlocked)}
                        onClick={handleRhdLabelClick}
                      >
                        <NewReqPatientTestPanelRhdOnlyCheckboxWrapper
                          deviceType={deviceType}
                          style={maleSexOpacitySx}
                        >
                          <NewReqPatientTestPanelCheckboxTitleWrapper
                            style={rhdBlockedOpacitySx}
                          >
                            <span>
                              <BoldText>RhD Only</BoldText>
                            </span>
                            <br />
                            <FormLabel sx={lightFormLabelSx}>
                              Carrier Screen and Aneuploidy NIPT will be opted
                              out
                            </FormLabel>
                          </NewReqPatientTestPanelCheckboxTitleWrapper>
                        </NewReqPatientTestPanelRhdOnlyCheckboxWrapper>
                      </StyledFormLabelClinical>
                    </Box>
                  </NewReqCheckboxLabelRow>
                </div>
              </Grid>
              <Divider sx={aquaDividerTightSx} />
            </>
          )}

          <Grid item xs={12} sx={flushTopSx}>
            {renderAneuploidyVariant(
              true,
              !requisitionForm.aneuploidyNIPT || Boolean(requisitionForm.is22q),
            )}
            <Divider sx={aneuploidyVariantDividerSx}>OR</Divider>
            {renderAneuploidyVariant(
              false,
              requisitionForm.aneuploidyNIPT && !requisitionForm.is22q,
            )}
          </Grid>

          <Grid item xs={12} sx={carrierSectionTopGapSx}>
            <div>
              <StyledSectionHeadingClinical
                data-testid="carrier-screen-label"
                sx={carrierHeadingOpacitySx}
              >
                Unity Carrier Screen
              </StyledSectionHeadingClinical>
              <TestPanelSectionStack>
                <CarrierScreenPanelSelect
                  disabled={requisitionForm.isRhdOnlyOrder}
                  carrierScreen={requisitionForm.carrierScreen}
                  expandedCarrierPanel={requisitionForm.expandedCarrierPanel}
                  onSelect={onCarrierPanelSelect}
                  visibleExpandedCarrierPanels={visibleExpandedCarrierPanels}
                  afterFivePanelContent={
                    requisitionForm.isFragileXAccount ||
                    isFiveGenePanelSelected ? (
                      <TestPanelNestedStack>
                        <Box sx={carrierAddOnsSx}>
                          {requisitionForm.isFragileXAccount && (
                            <NewReqTestOptionRow $nested>
                              <NewReqTestOptionLabelColumn
                                style={maleSexOpacitySx}
                              >
                                <CheckboxLabelWrapper
                                  label={
                                    <>
                                      <OptionLabel>
                                        Add <strong>Fragile X Syndrome</strong>
                                      </OptionLabel>
                                      <OptionGenes>FMR1</OptionGenes>
                                    </>
                                  }
                                  control={
                                    <Checkbox
                                      sx={unselectedCheckboxSx}
                                      checked={requisitionForm.isFragileX}
                                      disabled={
                                        carrierAddOnsDisabled ||
                                        requisitionForm.sex === "Male"
                                      }
                                      name="isFragileX"
                                      onChange={onChange}
                                    />
                                  }
                                />
                              </NewReqTestOptionLabelColumn>
                              <NewReqHelperTextBox>
                                <InfoOutlined />
                                <NewReqHelperTextLines>
                                  <HelperCopy>
                                    Carrier screening for X-linked conditions
                                    (FMR1 and DMD genes) is not performed for
                                    male patients, and cfDNA fetal risk
                                    assessment is provided via fetal sex, unless
                                    fetal-sex has been opted out.
                                  </HelperCopy>
                                </NewReqHelperTextLines>
                              </NewReqHelperTextBox>
                            </NewReqTestOptionRow>
                          )}
                          {(isFiveGenePanelSelected ||
                            requisitionForm.isFragileXAccount) &&
                            renderPlusPanelCheckbox()}
                          {requisitionForm.isFragileXAccount &&
                            requisitionForm.isAcogPlus &&
                            !requisitionForm.isFragileX &&
                            requisitionForm.sex !== "Male" && (
                              <NewReqNoteMessage $isError>
                                <OptionLabel $tone="danger">
                                  <strong>Note: </strong>
                                  Select all 3 boxes above,{" "}
                                  <strong>including Fragile X (FMR1)</strong>,
                                  if you would like to order a 14 gene panel.
                                </OptionLabel>
                              </NewReqNoteMessage>
                            )}
                          {isFiveGenePanelSelected &&
                            renderCfDnaOptOutCheckbox({ indent: true })}
                        </Box>
                      </TestPanelNestedStack>
                    ) : null
                  }
                  after130PanelContent={
                    is130GenePanelSelected
                      ? renderCfDnaOptOutCheckbox({ indent: true })
                      : null
                  }
                />
              </TestPanelSectionStack>
            </div>
          </Grid>
        </Grid>
        {showCarrierScreenNote && (
          <Box sx={{ mx: 5, mt: 2, pb: 2 }}>
            <NewReqNoteMessage $isError>
              <WarningIcon testId="CarrierScreenNoteWarningIcon" />
              {GetTypographyContent({
                content: "Select the ",
                size: "medium",
                textColor: "redDark",
              })}
              {GetTypographyContent({
                content: "Carrier Screen",
                size: "medium",
                textColor: "redDark",
                isBold: true,
              })}
              {GetTypographyContent({
                content:
                  " box above if you would also like to order Carrier Screening.",
                size: "medium",
                textColor: "redDark",
              })}
            </NewReqNoteMessage>
          </Box>
        )}
      </StyledBox>

      {requisitionForm.isMobilePhlebotomyAccount && (
        <StyledBox sx={phlebotomyBoxSx}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={isOtherLanguageSelected ? 5 : 7}>
              <NewReqCheckboxLabelRow>
                <SoloCheckboxWrapper>
                  <Checkbox
                    id="new-test-ci-phleb"
                    sx={flushCheckboxSx}
                    checked={requisitionForm.isMobilePhlebotomy}
                    name="isMobilePhlebotomy"
                    onChange={onChange}
                  />
                </SoloCheckboxWrapper>
                <Box sx={columnFlexSx}>
                  <StyledFormLabelClinical
                    sx={clickableClinicalLabelSx}
                    onClick={handlePhlebotomyLabelClick}
                  >
                    <BoldText>Request phlebotomy</BoldText>
                  </StyledFormLabelClinical>
                  <FormLabel sx={lightFormLabelSx}>
                    Please select if patient requires a phlebotomist.
                  </FormLabel>
                </Box>
              </NewReqCheckboxLabelRow>
            </Grid>

            {isPhlebotomyLanguageSelectEnabled.enabled &&
              shouldShowLanguageDropdown && (
                <Grid
                  item
                  xs={12}
                  md={isOtherLanguageSelected ? 7 : 5}
                  sx={getPhlebotomyLanguageRowSx(isOtherLanguageSelected)}
                >
                  <FormControl sx={phlebotomyLanguageSelectSx} size="small">
                    <InputLabel id="preferred-language-label">
                      Preferred Language
                    </InputLabel>
                    <Select
                      labelId="preferred-language-label"
                      id="preferred-language-select"
                      value={requisitionForm.preferredLanguage ?? ""}
                      label="Preferred Language"
                      disabled={preflight}
                      onChange={handlePreferredLanguageChange}
                    >
                      {phlebotomyLanguageOptions.map((lang) => (
                        <MenuItem key={lang} value={lang}>
                          {lang}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isOtherLanguageSelected && (
                    <TextField
                      sx={otherLanguageFieldSx}
                      size="small"
                      label="Other Language"
                      placeholder="Type here"
                      disabled={preflight}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={requisitionForm.otherLanguage ?? ""}
                      onChange={handleOtherLanguageChange}
                    />
                  )}
                </Grid>
              )}
          </Grid>
        </StyledBox>
      )}
    </>
  );
};
export default ClinicalIndicationTestPanel;
